/**
 * All studyRole CRUD actions
 *
 * Actions are payloads of information that send data from the application
 * (i.e. Yote server) to the store. They are the _only_ source of information
 * for the store.
 *
 * NOTE: In Yote, we try to keep actions and reducers dealing with CRUD payloads
 * in terms of 'item' or 'items'. This keeps the action payloads consistent and
 * aides various scoping issues with list management in the reducers.
 */

// import api utility
import callAPI from '../../global/utils/api'

const shouldFetchSingle = (state, id) => {
  /**
   * This is helper method to determine whether we should fetch a new single
   * user object from the server, or if a valid one already exists in the store
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetch single");
  const { byId, selected } = state.studyRole;
  if(selected.id !== id) {
    // the "selected" id changed, so we _should_ fetch
    // console.log("Y shouldFetch - true: id changed");
    return true;
  } else if(selected.isFetching) {
    // "selected" is already fetching, don't do anything
    // console.log("Y shouldFetch - false: isFetching");
    return false;
  } else if(!byId[id] && !selected.error) {
    // the id is not in the map, fetch from server
    // however, if the api returned an error, then it SHOULDN'T be in the map
    // so re-fetching it will result in an infinite loop
    // console.log("Y shouldFetch - true: not in map");
    return true;
  } else if(new Date().getTime() - selected.lastUpdated > (1000 * 60 * 5)) {
    // it's been longer than 5 minutes since the last fetch, get a new one
    // console.log("Y shouldFetch - true: older than 5 minutes");
    // also, don't automatically invalidate on server error. if server throws an error,
    // that won't change on subsequent requests and we will have an infinite loop
    return true;
  } else {
    // if "selected" is invalidated, fetch a new one, otherwise don't
    // console.log("Y shouldFetch - " + selected.didInvalidate + ": didInvalidate");
    return selected.didInvalidate;
  }
}

export const INVALIDATE_SELECTED_STUDY_ROLE = "INVALIDATE_SELECTED_STUDY_ROLE"
export function invalidateSelected() {
  return {
    type: INVALIDATE_SELECTED_STUDY_ROLE
  }
}

export const fetchSingleIfNeeded = (id) => (dispatch, getState) => {
  if (shouldFetchSingle(getState(), id)) {
    return dispatch(fetchSingleStudyRoleById(id))
  } else {
    return dispatch(returnSingleStudyRolePromise(id)); // return promise that contains studyRole
  }
}

export const returnSingleStudyRolePromise = (id) => (dispatch, getState) => {
  /**
   * This returns the object from the map so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. this is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */
  return new Promise((resolve, reject) => {
    resolve({
      type: "RETURN_SINGLE_STUDY_ROLE_WITHOUT_FETCHING"
      , id: id
      , item: getState().studyRole.byId[id]
      , success: true
    })
  });
}

export const REQUEST_SINGLE_STUDY_ROLE = "REQUEST_SINGLE_STUDY_ROLE";
function requestSingleStudyRole(id) {
  return {
    type: REQUEST_SINGLE_STUDY_ROLE
    , id
  }
}

export const RECEIVE_SINGLE_STUDY_ROLE = "RECEIVE_SINGLE_STUDY_ROLE";
function receiveSingleStudyRole(json) {
  return {
    type: RECEIVE_SINGLE_STUDY_ROLE
    , id: json.groupRole ? json.groupRole._id : null
    , item: json.groupRole
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function fetchSingleStudyRoleById(studyRoleId) {
  return dispatch => {
    dispatch(requestSingleStudyRole(studyRoleId))
    return callAPI(`/api/group-roles/${studyRoleId}`)
      .then(json => dispatch(receiveSingleStudyRole(json)))
  }
}

export const ADD_SINGLE_STUDY_ROLE_TO_MAP = "ADD_SINGLE_STUDY_ROLE_TO_MAP";
export function addSingleStudyRoleToMap(item) {
  return {
    type: ADD_SINGLE_STUDY_ROLE_TO_MAP
    , item
  }
}

export const REQUEST_SINGLE_STUDY_ROLE_BY_SLUG = "REQUEST_SINGLE_STUDY_ROLE_BY_SLUG";
function requestSingleStudyRoleBySlug(slug) {
  return {
    type: REQUEST_SINGLE_STUDY_ROLE_BY_SLUG
    , slug
  }
}

export function fetchSingleBySlug(studyRoleSlug) {
  return dispatch => {
    dispatch(requestSingleStudyRoleBySlug(studyRoleSlug))
    return callAPI(`/api/group-roles/by-slug/${studyRoleSlug}`)
      .then(json => dispatch(receiveSingleStudyRole(json)))
  }
}


export const REQUEST_CREATE_STUDY_ROLE = "REQUEST_CREATE_STUDY_ROLE";
function requestCreateStudyRole(studyRole) {
  return {
    type: REQUEST_CREATE_STUDY_ROLE
    , studyRole
  }
}

export const RECEIVE_CREATE_STUDY_ROLE = "RECEIVE_CREATE_STUDY_ROLE";
function receiveCreateStudyRole(json) {
  return {
    type: RECEIVE_CREATE_STUDY_ROLE
    , id: json.groupRole ? json.groupRole._id : null
    , item: json.groupRole
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendCreateStudyRole(data) {
  return dispatch => {
    dispatch(requestCreateStudyRole(data))
    return callAPI('/api/group-roles', 'POST', data)
      .then(json => dispatch(receiveCreateStudyRole(json)))
  }
}

export const REQUEST_UPDATE_STUDY_ROLE = "REQUEST_UPDATE_STUDY_ROLE";
function requestUpdateStudyRole(studyRole) {
  return {
    type: REQUEST_UPDATE_STUDY_ROLE
    , id: studyRole._id
    , studyRole
  }
}

export const RECEIVE_UPDATE_STUDY_ROLE = "RECEIVE_UPDATE_STUDY_ROLE";
function receiveUpdateStudyRole(json) {
  return {
    type: RECEIVE_UPDATE_STUDY_ROLE
    , id: json.groupRole ? json.groupRole._id : null
    , item: json.groupRole
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendUpdateStudyRole(data) {
  return dispatch => {
    dispatch(requestUpdateStudyRole(data))
    return callAPI(`/api/group-roles/${data._id}`, 'PUT', data)
      .then(json => dispatch(receiveUpdateStudyRole(json)))
  }
}

export const REQUEST_REMOVE_STUDY_ROLE = "REQUEST_REMOVE_STUDY_ROLE";
function requestRemoveStudyRole(studyRoleId) {
  return {
    type: REQUEST_REMOVE_STUDY_ROLE
    , studyRoleId
  }
}

export const RECEIVE_REMOVE_STUDY_ROLE = "RECEIVE_REMOVE_STUDY_ROLE";
function receiveRemoveStudyRole(json) {
  return {
    type: RECEIVE_REMOVE_STUDY_ROLE
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendRemoveStudyRole(studyId, id) {
  return dispatch => {
    dispatch(requestRemoveStudyRole(id))
    return callAPI(`/api/group-roles/${studyId}/${id}`, 'DELETE')
      .then(json => dispatch(receiveRemoveStudyRole(json)))
  }
}

export const REQUEST_DELETE_STUDY_ROLE = "REQUEST_DELETE_STUDY_ROLE";
function requestDeleteStudyRole(studyRoleId) {
  return {
    type: REQUEST_DELETE_STUDY_ROLE
    , studyRoleId
  }
}

export const RECEIVE_DELETE_STUDY_ROLE = "RECEIVE_DELETE_STUDY_ROLE";
function receiveDeleteStudyRole(json) {
  return {
    type: RECEIVE_DELETE_STUDY_ROLE
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendDelete(id) {
  return dispatch => {
    dispatch(requestDeleteStudyRole(id))
    return callAPI(`/api/group-roles/${id}`, 'DELETE')
      .then(json => dispatch(receiveDeleteStudyRole(json)))
  }
}


/**
 * STUDY_ROLE LIST ACTIONS
 */

const findListFromArgs = (state, listArgs) => {
  /**
   * Helper method to find appropriate list from listArgs.
   *
   * Because we nest studyRoleLists to arbitrary locations/depths,
   * finding the correct list becomes a little bit harder
   */
  // let list = Object.assign({}, state.studyRole.lists, {});
  let list = { ...state.studyRole.lists }
  for(let i = 0; i < listArgs.length; i++) {
    list = list[listArgs[i]];
    if(!list) {
      return false;
    }
  }
  return list;
}

const shouldFetchList = (state, listArgs) => {
  /**
   * Helper method to determine whether to fetch the list or not from arbitrary
   * listArgs
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetchList with these args ", listArgs, "?");
  const list = findListFromArgs(state, listArgs);
  // console.log("LIST in question: ", list);
  if(!list || !list.items) {
    // yes, the list we're looking for wasn't found
    // console.log("X shouldFetch - true: list not found");
    return true;
  } else if(list.isFetching) {
    // no, this list is already fetching
    // console.log("X shouldFetch - false: fetching");
    return false
  } else if(new Date().getTime() - list.lastUpdated > (1000 * 60 * 5)) {
    // yes, it's been longer than 5 minutes since the last fetch
    // console.log("X shouldFetch - true: older than 5 minutes");
    return true;
  } else {
    // maybe, depends on if the list was invalidated
    // console.log("X shouldFetch - " + list.didInvalidate + ": didInvalidate");
    return list.didInvalidate;
  }
}

export const fetchListIfNeeded = (...listArgs) => (dispatch, getState) => {
  if(listArgs.length === 0) {
    // If no arguments passed, make the list we want "all"
    listArgs = ["all"];
  }
  // // so the new api works without changing the listArgs in all the components
  // listArgs = listArgs.map(arg => arg.toString().replaceAll('study', 'group'));
  if(shouldFetchList(getState(), listArgs)) {
    return dispatch(fetchList(...listArgs));
  } else {
    return dispatch(returnStudyRoleListPromise(...listArgs));
  }
}

export const returnStudyRoleListPromise = (...listArgs) => (dispatch, getState) => {
  /**
   * This returns the list object from the reducer so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. This is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */

  // return the array of objects just like the regular fetch
  const state = getState();
  const listItemIds = findListFromArgs(state, listArgs).items
  const listItems = listItemIds.map(id => state.studyRole.byId[id]);

  return new Promise((resolve, reject) => {
    resolve({
      type: "RETURN_STUDY_ROLE_LIST_WITHOUT_FETCHING"
      , listArgs: listArgs
      , list: listItems
      , success: true
    })
  });
}

export const REQUEST_STUDY_ROLE_LIST = "REQUEST_STUDY_ROLE_LIST"
function requestStudyRoleList(listArgs) {
  return {
    type: REQUEST_STUDY_ROLE_LIST
    , listArgs
  }
}

export const RECEIVE_STUDY_ROLE_LIST = "RECEIVE_STUDY_ROLE_LIST"
function receiveStudyRoleList(json, listArgs) {
  return {
    type: RECEIVE_STUDY_ROLE_LIST
    , listArgs
    , list: json.groupRoles
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export const ADD_STUDY_ROLE_TO_LIST = "ADD_STUDY_ROLE_TO_LIST";
export function addStudyRoleToList(id, ...listArgs) {
  // console.log("Add studyRole to list", id);
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: ADD_STUDY_ROLE_TO_LIST
    , id
    , listArgs
  }
}

export const REMOVE_STUDY_ROLE_FROM_LIST = "REMOVE_STUDY_ROLE_FROM_LIST"
export function removeStudyRoleFromList(id, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ['all'];
  }
  return {
    type: REMOVE_STUDY_ROLE_FROM_LIST
    , id
    , listArgs
  }
}

export function fetchList(...listArgs) {
  return dispatch => {
    if(listArgs.length === 0) {
      // default to "all" list if we don't pass any listArgs
      listArgs = ["all"];
    }
    // // so the new api works without changing the listArgs in all the components
    // listArgs = listArgs.map(arg => arg.toString().replaceAll('study', 'group'));
    dispatch(requestStudyRoleList(listArgs))
    /**
     * determine what api route we want to hit
     *
     * NOTE: use listArgs to determine what api call to make.
     * if listArgs[0] == null or "all", return list
     *
     * if listArgs has 1 arg, return "/api/group-roles/by-[ARG]"
     *
     * if 2 args, additional checks required.
     *  if 2nd arg is a string, return "/api/group-roles/by-[ARG1]/[ARG2]".
     *    ex: /api/group-roles/by-category/:category
     *  if 2nd arg is an array, though, return "/api/group-roles/by-[ARG1]-list" with additional query string
     *
     * TODO:  make this accept arbitrary number of args. Right now if more
     * than 2, it requires custom checks on server
     */
    let apiTarget = "/api/group-roles";
    if(listArgs.length == 1 && listArgs[0] !== "all") {
      apiTarget += `/by-${listArgs[0]}`;
    } else if(listArgs.length == 2 && Array.isArray(listArgs[1])) {
      // length == 2 has it's own check, specifically if the second param is an array
      // if so, then we need to call the "listByValues" api method instead of the regular "listByRef" call
      // this can be used for querying for a list of studyRoles given an array of studyRole id's, among other things
      apiTarget += `/by-${listArgs[0]}-list?`;
      // build query string
      for(let i = 0; i < listArgs[1].length; i++) {
        apiTarget += `${listArgs[0]}=${listArgs[1][i]}&`
      }
    } else if(listArgs.length == 2) {
      // ex: ("author","12345")
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
    } else if(listArgs.length > 2) {
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
      for(let i = 2; i < listArgs.length; i++) {
        apiTarget += `/${listArgs[i]}`;
      }
    }
    return callAPI(apiTarget).then(
      json => dispatch(receiveStudyRoleList(json, listArgs))
    )
  }
}

/**
 * LIST UTIL METHODS
 */
export const SET_STUDY_ROLE_FILTER = "SET_STUDY_ROLE_FILTER"
export function setFilter(filter, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  // // so the new api works without changing the listArgs in all the components
  // listArgs = listArgs.map(arg => arg.toString().replaceAll('study', 'group'));
  return {
    type: SET_STUDY_ROLE_FILTER
    , filter
    , listArgs
  }
}

export const SET_STUDY_ROLE_PAGINATION = "SET_STUDY_ROLE_PAGINATION"
export function setPagination(pagination, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  // // so the new api works without changing the listArgs in all the components
  // listArgs = listArgs.map(arg => arg.toString().replaceAll('study', 'group'));
  return {
    type: SET_STUDY_ROLE_PAGINATION
    , pagination
    , listArgs
  }
}

export const INVALIDATE_STUDY_ROLE_LIST = "INVALIDATE_STUDY_ROLE_LIST"
export function invalidateList(...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  // // so the new api works without changing the listArgs in all the components
  // listArgs = listArgs.map(arg => arg.toString().replaceAll('study', 'group'));
  return {
    type: INVALIDATE_STUDY_ROLE_LIST
    , listArgs
  }
}




// WEBPACK FOOTER //
// modules/studyRole/studyRoleActions.js